import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet } from '../../../../generic/Api_Functions'
import { useSmartInterval } from '../../../../generic/hooks/useSmartInterval'
export const MosContext = createContext(null)

const ACTIVE_JOBS_POLL_MS = 2000
const ACTIVE_JOBS_PROGRESS_POLL_MS = 5000

export const DEFAULT_CONSUMPTION_MODE = 'FIFO'
export const DEFAULT_MACHINE_STORAGE = { present: false, consumptionMode: DEFAULT_CONSUMPTION_MODE }

const normalizeActiveJob = (item) => {
  if (!item) return null
  const topNode = item.node ?? null
  return {
    ...item,
    node: topNode,
    jobReference: item.jobReference
      ? { ...item.jobReference, node: item.jobReference.node ?? topNode }
      : item.jobReference
  }
}

// Story 113 (S157, R-E) — THE BLOCKER of the story. `applyActiveJobs` returns early when this
// signature has not changed, and until now it was only `fo.id:jr.id`. A mode transition (P->E or
// E->P) does NOT change the machine's active-job composition, so the context kept the old objects
// and the four station widgets would have shown the previous mode, the previous buttons, reporting
// still enabled and no label INDEFINITELY — even after `refreshActiveJobs()`. The module docs
// already noted this as a pre-existing limitation ("flipping a flag in Sun does not propagate");
// with this story it stops being tolerable and becomes a total functional failure.
//
// The status code is therefore part of the identity of an active job, not just of its payload.
// No new request: it is the same active-context poll (§8.2, S161).
//
// Story 120 (S167/S168, §7.2 #13) — the SAME blocker, this time for the batch. `batchId` (F3,
// `03-dominio.md` §8.1) travels in every entry of this very poll's `jobs[]`, but until now it played
// no part in the signature. A job joining a batch, or a batch ceasing to be current, does NOT change
// the fabrication order, the job or the status code either — exactly the shape of change #113 fixed
// for the mode, and left unfixed for the batch because the batch did not exist yet. Without this,
// the batch actions/notices would never appear or disappear on their own: the operator would have to
// leave the station and come back for `applyActiveJobs` to ever call `setActiveJobs` again. So the
// batch id is now part of the identity of an active job too, consumed verbatim (never derived here —
// Sun already resolved it in bulk per page, RN51). Still no new request.
export const computeActiveJobsSignature = (list) =>
  list
    .map((item) =>
      `${item?.fabricationOrder?.id ?? ''}:${item?.jobReference?.id ?? ''}:${item?.statusCode ?? ''}:${item?.batchId ?? ''}`)
    .join('|')

// #39 (Bloque A): `GET mos/device/{id}/active-context` moved from a bare jobs array to an object
// wrapping `{ device, machineStorage, jobs }` so a machine's Machine Storage presence/consumption
// mode travel alongside the active jobs in the same poll (S5) and are shared, server-sourced state
// for every widget on that machine (S4). Tolerant to both shapes on purpose (contrato-endpoints
// #39): a bare array is treated as "jobs only" so the front does not depend on the exact rollout
// order with Sun — defaults (not present, FIFO) apply until Sun ships the wrapped shape.
export const normalizeActiveContext = (result) => {
  if (Array.isArray(result)) {
    return { jobs: result, machineStorage: { ...DEFAULT_MACHINE_STORAGE } }
  }
  const jobs = Array.isArray(result?.jobs) ? result.jobs : []
  const machineStorage = {
    present: result?.machineStorage?.present ?? false,
    consumptionMode: result?.machineStorage?.consumptionMode ?? DEFAULT_CONSUMPTION_MODE
  }
  return { jobs, machineStorage }
}

// S7: fail-secure capability check — a worker with no resolvable `mosCapabilities` (not logged in
// yet, or the backend granted nothing) never gets a positive answer.
export const workerHasCapability = (worker, code) => {
  const capabilities = worker?.mosCapabilities
  return Array.isArray(capabilities) && capabilities.includes(code)
}

const MosInfoProvider = ({ children }) => {
  const mainContainerRef = useRef()
  const [widgetsLoaded, setWidgetsLoaded] = useState(false)
  const [device, setDevice] = useState(null)
  const [node, setNode] = useState(null)
  const [fabricationOrder, setFabricationOrder] = useState(null)
  const [jobReference, setJobReference] = useState(null)
  const [worker, setWorker] = useState(null)
  const [targetDeviceId, setTargetDeviceId] = useState(null)
  const [globalData, setGlobalData] = useState({})
  const [activeJobs, setActiveJobs] = useState([])
  const [activeJobsProgress, setActiveJobsProgress] = useState({})
  const [machineStorage, setMachineStorage] = useState(DEFAULT_MACHINE_STORAGE)
  const navigate = useNavigate()
  const timeoutRef = useRef(null)
  const [taskReference, setTaskReference] = useState(null)
  // Inspections are reported from more than one widget (the Inspection widget and the task selector
  // rows open the same dialog). Bumping this counter after a successful report gives every widget
  // that shows inspection progress a single signal to reload from, so their progress bars stay in
  // sync no matter which widget was used to fill the features.
  const [inspectionReportVersion, setInspectionReportVersion] = useState(0)

  const fabricationOrderRef = useRef(null)
  const jobReferenceRef = useRef(null)
  const activeJobsSignatureRef = useRef('')

  useEffect(() => { fabricationOrderRef.current = fabricationOrder }, [fabricationOrder])
  useEffect(() => { jobReferenceRef.current = jobReference }, [jobReference])
  const [pendingQualityIncidenceOpen, setPendingQualityIncidenceOpen] = useState(false)
  const [qualityIncidencePayload, setQualityIncidencePayload] = useState(null)

  const requestWorker = useCallback(() => {
    setGlobalData((prev) => {
      return { ...prev, workerRequested: true }
    })
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setGlobalData((prev) => {
        return { ...prev, workerRequested: false }
      })
    }, 1000)
  }, [])

  const onSetTargetDeviceId = useCallback((targetDeviceId) => {
    setWidgetsLoaded(false)
    setTargetDeviceId(targetDeviceId)
  }, [])

  const navigateToDeviceId = useCallback((deviceId) => {
    if (!deviceId) {
      if (targetDeviceId === null) {
        return
      }
      onSetTargetDeviceId(null)
      navigate('/mos/manufacturing')
      return
    }
    if (deviceId === targetDeviceId) {
      return
    }
    onSetTargetDeviceId(deviceId)
    navigate('/mos/manufacturing/device/' + deviceId)
  }, [navigate, onSetTargetDeviceId, targetDeviceId])

  useEffect(() => {
    const loadDevice = async () => {
      setDevice(await apiGet('device/byId/' + targetDeviceId))
    }
    setDevice(null)
    setNode(null)
    setFabricationOrder(null)
    setWorker(null)
    setJobReference(null)
    setTaskReference(null)
    setActiveJobs([])
    setMachineStorage(DEFAULT_MACHINE_STORAGE)
    activeJobsSignatureRef.current = ''
    if (targetDeviceId !== null) {
      loadDevice()
    }
  }, [targetDeviceId])

  const enableFullscreen = useCallback(() => {
    // TODO IMPROVE DIALOGOS NO FUNCIONAN ASI
    // mainContainerRef.current.requestFullscreen()
    document.documentElement.requestFullscreen()
  }, [])

  const selectNode = useCallback((node, fabricationOrder, jobReference) => {
    if (!node) {
      throw new Error('node cannot be null')
    }
    if (!fabricationOrder) {
      throw new Error('fabricationOrder cannot be null')
    }
    if (!jobReference) {
      throw new Error('jobReference cannot be null')
    }
    // TODO CHECK IF THEY HAVE ENOUGH PROPERTIES
    setNode(node)
    setFabricationOrder(fabricationOrder)
    // Se añade currentJobProgress a globalData porque son datos mutables, no estáticos como en JobReference
    setGlobalData(prev => ({ ...prev, currentNodeName: node?.name || null, currentJobProgress: null, showActiveJobsList: false }))
    setJobReference(jobReference)
    setTaskReference(null)
  }, [])

  const unSelectNode = useCallback(() => {
    setNode(null)
    setFabricationOrder(null)
    setGlobalData(prev => ({ ...prev, currentNodeName: null, currentJobProgress: null }))
    setJobReference(null)
    setTaskReference(null)
  }, [])

  const fetchActiveJobs = useCallback(async () => {
    if (targetDeviceId === null) return { jobs: [], machineStorage: DEFAULT_MACHINE_STORAGE }
    try {
      const result = await apiGet('mos/device/' + targetDeviceId + '/active-context')
      const { jobs, machineStorage: fetchedMachineStorage } = normalizeActiveContext(result)
      return { jobs: jobs.map(normalizeActiveJob), machineStorage: fetchedMachineStorage }
    } catch (err) {
      console.error('Error fetching active jobs', err)
      return { jobs: [], machineStorage: DEFAULT_MACHINE_STORAGE }
    }
  }, [targetDeviceId])

  const applyActiveJobs = useCallback((data) => {
    const list = (Array.isArray(data?.jobs) ? data.jobs : []).filter(Boolean)
    setMachineStorage(data?.machineStorage ?? DEFAULT_MACHINE_STORAGE)

    setGlobalData((prev) => {
      const qty = list.length
      if (prev?.activeJobQuantity === qty) return prev
      return { ...prev, activeJobQuantity: qty }
    })

    const currentFo = fabricationOrderRef.current
    const currentJr = jobReferenceRef.current
    const hasCurrentSelection = Boolean(currentFo?.id && currentJr?.id)
    const stillActive = hasCurrentSelection
      ? list.find((item) =>
        item?.fabricationOrder?.id === currentFo.id && item?.jobReference?.id === currentJr.id)
      : null

    if (hasCurrentSelection && !stillActive) {
      unSelectNode()
    }

    if (!stillActive && list.length >= 1) {
      const first = list[0]
      if (first?.node && first?.fabricationOrder && first?.jobReference) {
        selectNode(first.node, first.fabricationOrder, first.jobReference)
      }
    }

    const signature = computeActiveJobsSignature(list)
    if (signature === activeJobsSignatureRef.current) {
      return
    }
    activeJobsSignatureRef.current = signature
    setActiveJobs(list)
  }, [setGlobalData, unSelectNode, selectNode])

  const restartActiveJobsInterval = useSmartInterval(fetchActiveJobs, ACTIVE_JOBS_POLL_MS, applyActiveJobs)

  const refreshActiveJobs = useCallback(() => {
    restartActiveJobsInterval(true, true)
  }, [restartActiveJobsInterval])

  const fetchActiveJobsProgress = useCallback(async () => {
    if (!activeJobs || activeJobs.length === 0) return {}

    const entries = {}
    const foCache = {}

    await Promise.all(activeJobs.map(async (item) => {
      const foId = item.fabricationOrder?.id
      const jrId = item.jobReference?.id
      if (!foId || !jrId) return

      try {
        let response = foCache[foId]
        if (!response) {
          response = await apiGet(`mos/fabricationOrder/${foId}/progress`)
          foCache[foId] = response
        }

        const job = response?.jobs?.find(j => j.id === jrId)
        if (!job) return

        const available = job.available ?? 0
        const done = job.done ?? 0
        const fail = job.fail ?? 0
        const planned = job.quantity ?? 0
        const reported = done + fail
        const plannedEffective = planned > 0 ? planned : (reported + available)
        const percentage = plannedEffective > 0 ? (reported / plannedEffective) * 100 : 0

        const key = `${foId}-${jrId}`
        entries[key] = Math.round(percentage)
      } catch (error) {
        console.error('Error fetching job progress in provider', error)
      }
    }))

    return entries
  }, [activeJobs])

  const restartActiveJobsProgressInterval = useSmartInterval(fetchActiveJobsProgress, ACTIVE_JOBS_PROGRESS_POLL_MS, setActiveJobsProgress)

  const refreshActiveJobsProgress = useCallback(() => {
    restartActiveJobsProgressInterval(true, true)
  }, [restartActiveJobsProgressInterval])

  const notifyInspectionReported = useCallback(() => {
    setInspectionReportVersion((previous) => previous + 1)
  }, [])

  const requestOpenQualityIncidence = useCallback((payload = null) => {
    setQualityIncidencePayload(payload)
    setPendingQualityIncidenceOpen(true)
  }, [])

  const consumeQualityIncidenceOpen = useCallback(() => {
    setPendingQualityIncidenceOpen(false)
  }, [])

  const clearQualityIncidencePayload = useCallback(() => {
    setQualityIncidencePayload(null)
  }, [])

  // S7: exposed so any widget can gate a MOS capability-restricted action (here, changing the
  // consumption mode) without duplicating the fail-secure lookup logic.
  const hasCapability = useCallback((code) => workerHasCapability(worker, code), [worker])

  const value = useMemo(() => {
    return {
      enableFullscreen,
      device,
      setTargetDeviceId: onSetTargetDeviceId,
      widgetsLoaded,
      setWidgetsLoaded,
      navigateToDeviceId,
      node,
      fabricationOrder,
      jobReference,
      worker,
      setWorker,
      selectNode,
      unSelectNode,
      globalData,
      setGlobalData,
      requestWorker,
      mainContainerRef,
      taskReference,
      setTaskReference,
      inspectionReportVersion,
      notifyInspectionReported,
      activeJobs,
      refreshActiveJobs,
      activeJobsProgress,
      refreshActiveJobsProgress,
      machineStorage,
      hasCapability,
      pendingQualityIncidenceOpen,
      requestOpenQualityIncidence,
      consumeQualityIncidenceOpen,
      qualityIncidencePayload,
      clearQualityIncidencePayload
    }
  }, [device, widgetsLoaded, setWidgetsLoaded, navigateToDeviceId, fabricationOrder, jobReference, node, worker, enableFullscreen, onSetTargetDeviceId, selectNode, unSelectNode, globalData, setGlobalData, requestWorker, taskReference, setTaskReference, inspectionReportVersion, notifyInspectionReported, activeJobs, refreshActiveJobs, activeJobsProgress, refreshActiveJobsProgress, machineStorage, hasCapability, pendingQualityIncidenceOpen, requestOpenQualityIncidence, consumeQualityIncidenceOpen, qualityIncidencePayload, clearQualityIncidencePayload])
  return (
    <MosContext.Provider value={value}>
      <div ref={mainContainerRef} style={{ height: '100%' }}>
        {children}
      </div>
    </MosContext.Provider>
  )
}

export default MosInfoProvider
